/** Formulário do briefing (diagnóstico high-ticket). A cliente preenche. Fase 2. */
import { useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { Link, useNavigate } from "react-router";
import { BRIEFING_SCHEMA } from "@unik/shared";
import { api } from "../../lib/api";
import { BrandMark } from "../../ui/BrandMark";
import { Button } from "../../ui/Button";
import { Input, TextArea } from "../../ui/Input";
import { Select } from "../../ui/Select";
import { FullScreenLoader } from "../../ui/ProtectedRoute";
import { FloatingWhatsApp, WhatsAppIcon, UNIK_WHATSAPP_PHONE } from "../../ui/FloatingWhatsApp";

interface BriefingDTO {
  responses: Record<string, string>;
  status: "draft" | "submitted";
  submittedAt: string | null;
}

export function Briefing() {
  const navigate = useNavigate();
  const [responses, setResponses] = useState<Record<string, string> | null>(null);
  const [status, setStatus] = useState<"draft" | "submitted">("draft");
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const saveTimer = useRef<number | undefined>(undefined);
  const dirty = useRef(false);

  useEffect(() => {
    api
      .get<BriefingDTO>("/approval/briefing")
      .then((d) => {
        setResponses(d.responses);
        setStatus(d.status);
      })
      .catch(() => setResponses({}));
  }, []);

  useEffect(() => {
    if (responses === null || !dirty.current || status === "submitted") return;
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      setSaving(true);
      api
        .put("/approval/briefing", { responses, submit: false })
        .catch(() => undefined)
        .finally(() => setSaving(false));
      dirty.current = false;
    }, 900);
    return () => window.clearTimeout(saveTimer.current);
  }, [responses, status]);

  function set(key: string) {
    return (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      dirty.current = true;
      setResponses((r) => ({ ...(r ?? {}), [key]: e.target.value }));
    };
  }

  async function enviar() {
    setSaving(true);
    try {
      await api.put("/approval/briefing", { responses: responses ?? {}, submit: true });
      setStatus("submitted");
      setSent(true);
    } finally {
      setSaving(false);
    }
  }

  if (responses === null) return <FullScreenLoader />;

  if (status === "submitted") {
    return (
      <div className="app-shell flex min-h-dvh flex-col items-center justify-center px-6 text-center">
        <div className="w-full max-w-sm">
          <BrandMark variant="vinho" className="mx-auto h-6" />
          <h1 className="type-h2 mt-4 text-text-cream">
            {sent ? "Recebido! 💛" : "Briefing enviado"}
          </h1>
          <p className="type-body mt-3 text-text-secondary">
            Obrigada por preencher. A gente já está usando essas respostas no seu planejamento.
          </p>
          <div className="mt-8 flex flex-col gap-3">
            <a
              href={`https://wa.me/${UNIK_WHATSAPP_PHONE}?text=${encodeURIComponent(
                "Olá Lara! Acabei de enviar o meu briefing pelo portal Unik. As respostas já estão disponíveis na plataforma."
              )}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(37,211,102,0.35)] hover:bg-[#20ba59] hover:scale-[1.02] active:scale-95 transition-all"
            >
              <WhatsAppIcon className="h-5 w-5 text-white shrink-0" />
              <span>Enviar no WhatsApp da Lara</span>
            </a>
            <Link to="/aprovacoes">
              <Button variant="secondary" fullWidth>Ir para minha área</Button>
            </Link>
          </div>
        </div>
        <FloatingWhatsApp phone={UNIK_WHATSAPP_PHONE} message="Olá Lara! Acabei de enviar o briefing da Unik." label="Avisar no WhatsApp" />
      </div>
    );
  }

  return (
    <div className="app-shell min-h-dvh px-5 py-6">
      <div className="mx-auto w-full max-w-lg">
        <BrandMark variant="vinho" className="h-6" />
        <h1 className="type-h2 mt-3 text-text-cream">Seu diagnóstico</h1>
        <p className="type-body-sm mt-2 text-text-secondary">
          Quanto mais completo, melhor o seu conteúdo. Pode preencher aos poucos — a gente salva
          sozinho. {saving && <span className="text-text-tertiary">Salvando…</span>}
        </p>

        <div className="mt-6 flex flex-col gap-8">
          {BRIEFING_SCHEMA.map((section) => (
            <section key={section.key}>
              <h2 className="type-h3 text-text-cream">{section.title}</h2>
              <p className="type-caption mt-1 text-text-tertiary">{section.description}</p>
              <div className="mt-4 flex flex-col gap-4">
                {section.fields.map((f) => {
                  if (f.type === "textarea") {
                    return (
                      <TextArea
                        key={f.key} label={f.label} value={responses[f.key] ?? ""}
                        onChange={set(f.key)} placeholder={f.placeholder}
                      />
                    );
                  }
                  if (f.type === "select") {
                    return (
                      <Select key={f.key} label={f.label} value={responses[f.key] ?? ""} onChange={set(f.key)}>
                        <option value="">Selecione…</option>
                        {(f.options ?? []).map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </Select>
                    );
                  }
                  return (
                    <Input
                      key={f.key} label={f.label} value={responses[f.key] ?? ""}
                      onChange={set(f.key)} placeholder={f.placeholder}
                    />
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-3">
          <Button size="lg" shape="invite" onClick={enviar} loading={saving}>
            Enviar diagnóstico
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate("/aprovacoes")}>
            Continuar depois
          </Button>
        </div>
      </div>
      <FloatingWhatsApp phone={UNIK_WHATSAPP_PHONE} message="Olá Lara! Estou preenchendo o diagnóstico/briefing da Unik e gostaria de tirar uma dúvida." label="Falar com a Lara" />
    </div>
  );
}
